import React, { useState } from 'react';
import { Card, Checkbox, Select } from 'ui-component-library';
import { oneOfType, arrayOf } from 'prop-types';

import connectionStatusActivityShape from '../../../../shapes/connection-status-activity';
import updateStatusActivityShape from '../../../../shapes/status-update-activity';
import problemActivityShape from '../../../../shapes/problem-activity';
import InfoSection from '../card-shared-components/card-info-section';
import Activity from '../activities/activity';
import { TOOLTIP_MESSAGES, TRACKING_EVENT_TYPES } from '../../../../constants';
import {
  ActivityTypeFilterContainer,
  FilterContainer,
  AutomatedEventsFilterContainer,
  ContentContainer,
} from './card-activity-history.style';
import theme from '../../../../theme';
import InfoTooltipButton from '../info-tooltip-button/info-tooltip-button';

/**
* Filters a list of activities to passed in event types.
* @param {object[]} unfilteredActivities - Unfiltered list of activities
* @param {string[]} types - List of types to include in filter
* @return {object[]} - Filtered activities list
*/
export const isActivityType = (activity, types) => {
  if (!types || types.length === 0) {
    return true;
  }
  return types.includes(activity.eventType);
};

/**
 * The activity history widget
 * @param {object} params - Object containing props
 * @param {object[]} activities - list of activities to show
 * @return {element}
 */
const ActivityHistory = ({ activities }) => {
  /**
   * Type Filter definition
   */
  const activityTypeFilterOptions = [{
    label: 'All Activity',
    value: 'All Activity',
    filter: (activity) => (isActivityType(activity, null)),
  }, {
    label: 'Tracking Events',
    value: 'Tracking Events',
    filter: (activity) => (isActivityType(activity, [
      TRACKING_EVENT_TYPES.manualTrack,
      TRACKING_EVENT_TYPES.automaticTrack,
    ])),
  }, {
    label: 'Load Status Updates',
    value: 'Load Status Updates',
    filter: (activity) => (isActivityType(activity, [
      TRACKING_EVENT_TYPES.statusUpdate,
    ])),
  }, {
    label: 'Logged Problems',
    value: 'Logged Problems',
    filter: (activity) => (isActivityType(activity, [
      TRACKING_EVENT_TYPES.problemSet,
    ])),
  }, {
    label: 'Resolved Problems',
    value: 'Resovled Problems',
    filter: (activity) => (isActivityType(activity, [
      TRACKING_EVENT_TYPES.problemResolution,
    ])),
  }, {
    label: 'Visibility App Connection Updates',
    value: 'Visibility App Connection Updates',
    filter: (activity) => (isActivityType(activity, [
      TRACKING_EVENT_TYPES.visibilityAppStatusUpdate,
    ])),
  },
  {
    label: 'Transit (Internal) Notes',
    value: 'Transit (Internal) Notes',
    filter: (activity) => (isActivityType(activity, [
      TRACKING_EVENT_TYPES.tmsInternalNote,
    ])),
  },
  {
    label: 'Bounced Load',
    value: 'Bounced Load',
    filter: (activity) => (isActivityType(activity, [
      TRACKING_EVENT_TYPES.bounce,
      TRACKING_EVENT_TYPES.rebooked,
    ])),
  }];

  // Type Filter state
  const [activityTypeFilter, setActivityTypeFilter] = useState(activityTypeFilterOptions[0]);
  const [automatedEventsFilter, setAutomatedEventsFilter] = useState(true);

  /**
   * Handles when the activity type filter changes
   * @param {object} value - New Type filter
   * @return {void}
   */
  const handleTypeFilterValueChange = (value) => {
    setActivityTypeFilter(value);
  };

  /**
   * Helper function to apply filters on the current activites
   * @return {object[]} - Filtered activities list (from prop)
   */
  const applyFilters = () => {
    if (!activities) {
      return [];
    }

    return activities.filter((activity) => (
      activityTypeFilter.filter(activity) && (automatedEventsFilter ? true : !activity.isAutomated)
    ));
  };

  /**
   * Handles scrolling to an activity
   * @param {string} eventId - ID of event from activity
   * @return {void}
   */
  const scrollToActivity = (eventId) => {
    const target = document.getElementById(eventId).parentNode;
    target.parentNode.scrollTop = target.offsetTop - target.parentNode.offsetTop;
  };

  // Applying filters before render
  const filteredActivites = !activities ? [] : applyFilters();
  return (
    <Card
      title="Activity History"
      headerContent={<InfoTooltipButton text={TOOLTIP_MESSAGES['activity-history']} isPrimary />}
      isPaddingRemoved
    >
      <div>
        <FilterContainer>
          <AutomatedEventsFilterContainer>
            <Checkbox
              checked={automatedEventsFilter}
              onValueChange={() => setAutomatedEventsFilter(!automatedEventsFilter)}
              text="Show Automated Updates"
              labelColor={theme.palette.black[100]}
            />
          </AutomatedEventsFilterContainer>
          <ActivityTypeFilterContainer>
            <Select
              selectedValue={activityTypeFilter}
              options={activityTypeFilterOptions}
              onValueChange={handleTypeFilterValueChange}
            />
          </ActivityTypeFilterContainer>
        </FilterContainer>
      </div>
      <ContentContainer>
        {filteredActivites.map((activity, i) => (
          <InfoSection key={activity.eventId} removePadding outlineBottom={i !== filteredActivites.length - 1}>
            <Activity
              activity={activity}
              options={{ errorBackgroundForProblems: true }}
              scrollToActivity={scrollToActivity}
            />
          </InfoSection>
        ))}
        {(filteredActivites.length === 0) && (
          <InfoSection>
            <Activity
              activity={null}
              options={{ noContentMessage: 'No activity to show on this load.' }}
            />
          </InfoSection>
        )}
      </ContentContainer>

    </Card>
  );
};

ActivityHistory.propTypes = {
  activities: arrayOf(oneOfType([
    connectionStatusActivityShape,
    updateStatusActivityShape,
    problemActivityShape,
    connectionStatusActivityShape,
  ])),
};

ActivityHistory.defaultProps = {
  activities: [],
};

export default ActivityHistory;
